// components/CustomScriptRenderer.tsx
'use client';

import React, { useEffect } from 'react';

interface ScriptCard {
  _uid: string;
  component?: string;
  placement: 'head' | 'body';
  script_code: string;
}

interface CustomScriptBlock {
  component?: string;
  all_scripts?: ScriptCard[];
}

interface CustomScriptRendererProps {
  customScriptBlock: CustomScriptBlock;
}

export const CustomScriptRenderer: React.FC<CustomScriptRendererProps> = ({ customScriptBlock }) => {
  useEffect(() => {
    if (!customScriptBlock?.all_scripts?.length) return;

    const injectedScripts = new Set<HTMLScriptElement>();

    customScriptBlock.all_scripts.forEach(scriptCard => {
      if (!scriptCard.script_code) return;

      const div = document.createElement('div');
      div.innerHTML = scriptCard.script_code;

      const scriptElements = div.querySelectorAll('script');
      scriptElements.forEach(scriptElement => {
        const newScript = document.createElement('script');

        Array.from(scriptElement.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });
        newScript.textContent = scriptElement.textContent;

        newScript.setAttribute('data-script-uid', scriptCard._uid);

        const target = scriptCard.placement === 'head' ? document.head : document.body;
        target.appendChild(newScript);

        injectedScripts.add(newScript);
      });

      const otherElements = Array.from(div.children).filter(el => el.tagName !== 'SCRIPT');
      otherElements.forEach(element => {
        const target = scriptCard.placement === 'head' ? document.head : document.body;
        const clone = element.cloneNode(true);
        if (clone instanceof Element) {
          clone.setAttribute('data-script-uid', scriptCard._uid);
          target.appendChild(clone);
        }
      });
    });

    return () => {
      injectedScripts.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });

      document.querySelectorAll('[data-script-uid]').forEach(el => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
    };
  }, [customScriptBlock]);

  return null;
};